import { useState, forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";

/**
 * Input コンポーネントのProps
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
    /** ラベルテキスト */
    label?: string;
    /** プレースホルダーテキスト */
    placeholder?: string;
    /** パスワード入力モード（●で隠す） */
    isPassword?: boolean;
    /** パスワード表示/非表示トグルを表示 */
    showPasswordToggle?: boolean;
    /** エラーメッセージ */
    error?: string;
    /** ヘルプテキスト */
    helpText?: string;
    /** 右側に表示するアイコン */
    rightIcon?: ReactNode;
    /** 右側アイコンクリック時のハンドラ */
    onRightIconClick?: () => void;
    /** サイズ */
    size?: "sm" | "md" | "lg";
    /** フルwidth */
    fullWidth?: boolean;
    /** 無効状態 */
    disabled?: boolean;
}

/**
 * Input - テキスト入力コンポーネント
 *
 * @description
 * ラベル、プレースホルダー、パスワード表示/非表示、エラー表示などをサポート
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            placeholder,
            isPassword = false,
            showPasswordToggle = false,
            error,
            helpText,
            rightIcon,
            onRightIconClick,
            size = "md",
            fullWidth = true,
            disabled = false,
            className,
            type,
            ...props
        },
        ref
    ) => {
        const [isPasswordVisible, setIsPasswordVisible] = useState(false);

        const handleTogglePassword = () => {
            setIsPasswordVisible((prev) => !prev);
        };

        // サイズに応じたクラス
        const sizeClasses = {
            sm: "px-3 py-1.5 text-sm",
            md: "px-4 py-2.5 text-base",
            lg: "px-4 py-3 text-lg",
        };

        // 入力タイプの決定
        const inputType = isPassword ? (isPasswordVisible ? "text" : "password") : type || "text";

        return (
            <div className={clsx("flex flex-col gap-1", fullWidth && "w-full")}>
                {/* ラベル */}
                {label && (
                    <label className="text-sm font-medium text-gray-700">{label}</label>
                )}

                {/* 入力フィールド */}
                <div className="relative">
                    <input
                        ref={ref}
                        type={inputType}
                        placeholder={placeholder}
                        disabled={disabled}
                        className={clsx(
                            "w-full rounded-md border transition-colors duration-200",
                            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
                            "[&::-ms-reveal]:hidden [&::-webkit-credentials-auto-fill-button]:hidden",
                            sizeClasses[size],
                            error
                                ? "border-error-500 focus:ring-error-500 focus:border-error-500"
                                : "border-gray-300 hover:border-gray-400",
                            disabled && "bg-gray-100 cursor-not-allowed opacity-60",
                            (rightIcon || (isPassword && showPasswordToggle)) && "pr-12",
                            className
                        )}
                        {...props}
                    />

                    {/* 右側アイコン or パスワードトグル */}
                    {(rightIcon || (isPassword && showPasswordToggle)) && (
                        <button
                            type="button"
                            onClick={
                                isPassword && showPasswordToggle
                                    ? handleTogglePassword
                                    : onRightIconClick
                            }
                            className={clsx(
                                "absolute right-3 top-1/2 -translate-y-1/2",
                                "text-gray-500 hover:text-gray-700 transition-colors",
                                disabled && "cursor-not-allowed"
                            )}
                            disabled={disabled}
                            tabIndex={-1}
                        >
                            {isPassword && showPasswordToggle ? (
                                isPasswordVisible ? (
                                    <EyeOffIcon className="w-5 h-5" />
                                ) : (
                                    <EyeIcon className="w-5 h-5" />
                                )
                            ) : (
                                rightIcon
                            )}
                        </button>
                    )}
                </div>

                {/* エラーメッセージ */}
                {error && <span className="text-sm text-error-500">{error}</span>}

                {/* ヘルプテキスト */}
                {helpText && !error && (
                    <span className="text-sm text-gray-500">{helpText}</span>
                )}
            </div>
        );
    }
);

Input.displayName = "Input";

// アイコンコンポーネント
const EyeIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
        />
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
    </svg>
);

const EyeOffIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className={className}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
        />
    </svg>
);

export default Input;
